import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Components
import EmojiPicker from 'components/form/EmojiPicker';
import Card from 'components/Layout/Card';
import LineTextLabel from 'components/Layout/LineTextLabel';
import Loader from 'components/Layout/Loader';
import Profile from 'components/Layout/Profile';
import Button from 'components/form/Button';

// Utils & Actions
import { icons } from 'utils/constants';
import { getBase64, getDataFromLocalStorage } from 'utils/helpers';
import { setOpenChatPopup, throwError } from 'store/slices';
import {
  sendWpChatMessages,
  fetchWpChatData,
  fetchInputWindowStatus,
} from 'store/slices/whatsappChatSlice';
import WpMessageBlock from './WpMessageBlock';
import { useParams } from 'react-router-dom';

const ChatContainer = ({
  name,
  paperId,
  whatsapp_number,
  user_id,
  isChatLoader,
  setIsChatLoader = () => {},
  profile_photo_path,
}) => {
  const params = useParams();
  const { type } = params;
  const reduxData = useSelector((state) => state.global);
  const { openChatPopup } = reduxData || {};

  // Refs
  const msgRef = useRef(null);
  const htmlElRef = useRef(null);
  const fileInputRef = useRef(null);

  // Individual state hooks
  const [msg, setMsg] = useState('');
  const [fileLoader, setFileLoader] = useState(false);
  const [activeStatus, setActiveStatus] = useState(false);
  const [messageList, setMessageList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('Msg');
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isInputWindow, setIsInputWindow] = useState(false);
  const [echoInstance, setEchoInstance] = useState(null);
  const [chatId, setChatId] = useState(null);

  const dispatch = useDispatch();
  const userData = getDataFromLocalStorage();
  const recieverName = name || '';

  // Helper functions
  const groupMessagesByDate = (messages) => {
    return messages?.reduce((acc, message) => {
      const messageDate = moment(message.created_at);
      let formattedDate;

      if (messageDate?.isSame(moment(), 'day')) {
        formattedDate = 'Today';
      } else if (messageDate?.isSame(moment().subtract(1, 'days'), 'day')) {
        formattedDate = 'Yesterday';
      } else {
        formattedDate = messageDate?.format('MMM DD, YYYY');
      }

      if (!acc[formattedDate]) {
        acc[formattedDate] = [];
      }
      acc[formattedDate].push(message);

      return acc;
    }, {});
  };

  const calculateUnreadMessages = (messages) => {
    return messages?.filter(
      (message) =>
        message?.status !== '2' && message?.message_origin !== 'AGENT'
    ).length;
  };

  const getLastUnreadMessages = (messages) => {
    const unreadMessages = messages?.filter(
      (message) =>
        message?.status !== '2' && message?.message_origin !== 'AGENT'
    );
    const count = unreadMessages?.length > 0 ? -unreadMessages?.length : -1;
    return unreadMessages?.slice(count);
  };

  // Derived values
  const groupedMessages = groupMessagesByDate(messageList);
  const unreadCount = calculateUnreadMessages(messageList);
  const lastUnreadMessages = getLastUnreadMessages(messageList);

  // API calls
  const getWpChatData = async () => {
    const payload = { phone_no: whatsapp_number, user_id };
    const response = await dispatch(fetchWpChatData(payload));

    if (response.status === 200) {
      setMessageList(response?.data?.whatsapp_chat);
      setIsLoading(false);
      setIsChatLoader(false);
      setChatId(response.data.chat_id);
    }
  };

  // Event handlers
  const handelSendMessage = async () => {
    setIsLoading(true);
    if (!msg.trim() && !selectedFile) return;

    const newMessage = {
      id: messageList.length + 1,
      message_origin: 'AGENT',
      message: msg,
      created_at: new Date().toISOString(),
      message_file: selectedFile || '',
      status: '0',
    };

    const response = await dispatch(
      sendWpChatMessages({
        message: msg || '',
        message_file: selectedFile || '',
        phone_no: whatsapp_number,
        user_id,
        type: fileType,
      })
    );

    if (response.status === 200) {
      if (fileType === 'Msg') {
        setMessageList([...messageList, newMessage]);
      } else {
        await getWpChatData();
      }

      setMsg('');
      setFileLoader(false);
      clearSelectedFile();
      setIsLoading(false);
    }

    setIsLoading(false);
  };

  const handleUploadChatFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const type = file.name.split('.').pop().toLowerCase();
    const allowedTypes = ['png', 'jpg', 'jpeg'];

    if (!allowedTypes.includes(type)) {
      dispatch(
        throwError({
          message: 'Invalid file selection. Accept only png, jpg, jpeg',
        })
      );
      return;
    }

    try {
      setFileLoader(true);
      const base64File = await getBase64(file);

      setSelectedFileName(file.name);
      setSelectedFile(base64File);
      setFileType('Image');
      setFileLoader(false);
    } catch (error) {
      dispatch(throwError({ message: 'Failed to process file' }));
      setFileLoader(false);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setSelectedFileName('');
    setFileType('Msg');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const fetchInputWindowStatusData = async () => {
    let payload = { phone_no: whatsapp_number, user_id };
    const response = await dispatch(fetchInputWindowStatus(payload));
    if (response.status === 200) {
      setIsInputWindow(response?.data?.isOpen);
    }
  };

  // Effects
  useEffect(() => {
    getWpChatData();
    fetchInputWindowStatusData();
  }, [whatsapp_number, user_id]);

  useEffect(() => {
    if (msgRef.current) {
      msgRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messageList]);

  useEffect(() => {
    // Initialize Pusher only once
    if (!window.Pusher) {
      window.Pusher = Pusher;
    }

    const echo = new Echo({
      broadcaster: 'pusher',
      key: process.env.REACT_APP_PUSHER_KEY,
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
      forceTLS: true,
      disableStats: true,
    });

    setEchoInstance(echo);

    // Subscribe to a unique channel based on chatId
    const channel = echo.channel(`whatsapp-channel-${chatId}`);
    channel.listen('.NewMessageSent', getWpChatData);

    return () => {
      if (echoInstance) {
        echoInstance.leaveChannel(`whatsapp-channel-${chatId}`);
      }
    };
  }, [chatId]);

  return (
    <div id="wp-chat-container">
      <Card className="cpb-16 h-100 chat-message-container">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between cps-20 cpe-20 cpb-16 cpt-16 border-bottom-prof bg-effed">
          <div className="d-flex align-items-center chat-profile-container cps-15">
            <div className="profile-container">
              <Profile
                isRounded
                text={recieverName}
                size="s-48"
                url={profile_photo_path}
                isS3UserURL
                bgColor="bg-a35b"
                textColor="color-white"
              />
              <div
                className={`status-icon ${activeStatus ? 'online' : 'offline'}`}
              ></div>
            </div>
            <div className="user-details-block ms-3">
              <div className="text-15-600 title-text">{recieverName}</div>
            </div>
          </div>
          {!openChatPopup && type !== 'whatsapp' && (
            <div
              className="cpe-30"
              onClick={() => dispatch(setOpenChatPopup(true))}
            >
              {/* <i className="bi bi-three-dots-vertical text-16-400 color-dark-silver" /> */}
              <img src={icons.zoomVector} alt="" />
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="chat iferp-scroll">
          {isChatLoader ? (
            <div>
              <Loader />
            </div>
          ) : (
            <>
              <div className="chat__wrapper">
                {groupedMessages &&
                  Object?.entries(groupedMessages)?.map(([date, messages]) => (
                    <React.Fragment key={date}>
                      <div className="cmt-24 cmb-24">
                        <LineTextLabel
                          text={
                            <span className="text-13-400 color-6866">
                              {date}
                            </span>
                          }
                          showBorder={false}
                        />
                      </div>

                      {messages?.map((elm, index) => {
                        const isAccess = elm.message_origin === 'AGENT';
                        const isLastUnread = lastUnreadMessages.includes(elm);
                        return (
                          <React.Fragment key={elm.id}>
                            {/* {isLastUnread && (
                        <div className="cmt-10 cmb-10">
                          <LineTextLabel
                            text={
                              <span className="text-14-500 color-1717">{`${unreadCount} Unread Messages`}</span>
                            }
                            showBorder={false}
                          />
                        </div>
                      )} */}

                            {/* ${
                          elm.message_file && isAccess
                            ? 'chat__message__msg_file'
                            : ''
                        } */}
                            <div
                              className={`chat__message ${
                                isAccess ? 'chat__message__own' : ''
                              } `}
                            >
                              <WpMessageBlock
                                data={elm}
                                readTickerShow={isAccess}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                <div ref={msgRef} />
              </div>
            </>
          )}
        </div>

        {/* Input Area */}

        {isInputWindow ? (
          <div className="chat-input-container position-relative">
            {selectedFile && (
              <div className="file-preview-card mb-2 p-2">
                <div className="d-flex align-items-center justify-content-between">
                  {fileType === 'Image' ? (
                    <div className="d-flex align-items-center">
                      <img
                        src={selectedFile}
                        alt="Preview"
                        className="img-thumbnail me-2"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                        }}
                      />
                      <span
                        className="text-truncate"
                        style={{ maxWidth: '150px' }}
                      >
                        {selectedFileName}
                      </span>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <i className="bi bi-file-earmark me-2"></i>
                      <span
                        className="text-truncate"
                        style={{ maxWidth: '200px' }}
                      >
                        {selectedFileName}
                      </span>
                    </div>
                  )}
                  <Button
                    onClick={clearSelectedFile}
                    icon={
                      <i className="bi bi-x-circle-fill text-danger me-2"></i>
                    }
                  />
                </div>
              </div>
            )}

            <div className="chat-i-block">
              <div className="left-emoji-icon">
                <EmojiPicker
                  onChange={(e) => setMsg((prev) => `${prev} ${e?.value}`)}
                />
              </div>
              <input
                autoFocus
                value={msg}
                ref={htmlElRef}
                id="post-input"
                placeholder="Write your message"
                onChange={(e) => setMsg(e.target.value)}
                onKeyUp={(e) => e.key === 'Enter' && handelSendMessage()}
              />

              <div className="create-post-icon-container">
                <label className="create-post-icon">
                  <span>
                    {fileLoader ? (
                      <Loader size="sm" />
                    ) : (
                      <img
                        src={icons.attchment}
                        alt="attachment"
                        className="create-post-icon"
                      />
                    )}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="Select File"
                    id="fileToUpload"
                    onChange={handleUploadChatFile}
                    accept=".jpg,.png,.jpeg"
                    hidden
                  />
                </label>

                <span
                  className={`post-send pointer ${
                    !msg.trim() && !selectedFile && isLoading ? 'disabled' : ''
                  }`}
                  onClick={
                    !msg.trim() && !selectedFile ? null : handelSendMessage
                  }
                  style={{
                    pointerEvents:
                      !msg.trim() && !selectedFile ? 'none' : 'auto',
                  }}
                >
                  {isLoading ? (
                    <Loader size="sm" />
                  ) : (
                    <i
                      className={`bi bi-send ${
                        !msg.trim() && !selectedFile ? 'text-muted' : ''
                      }`}
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="chat-input-container position-relative">
            <div className="cps-10 cpt-10">Reply window closed</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChatContainer;

// ----------------------------------------------------------------------------------------------------

// import React, { useState, useRef, useEffect, useCallback } from 'react';
// import { useDispatch } from 'react-redux';
// import moment from 'moment';
// import Echo from 'laravel-echo';
// import Pusher from 'pusher-js';

// // Components
// import EmojiPicker from 'components/form/EmojiPicker';
// import Card from 'components/Layout/Card';
// import LineTextLabel from 'components/Layout/LineTextLabel';
// import Loader from 'components/Layout/Loader';
// import Profile from 'components/Layout/Profile';
// import MessageBlock from 'pages/Common/NetworkManagement/Network/Chat/MessageBlock';

// // Utils & Actions
// import { icons } from 'utils/constants';
// import {
//   getBase64,
//   getDataFromLocalStorage,
//   objectToFormData,
// } from 'utils/helpers';
// import { throwError } from 'store/slices';
// import {
//   sendWpChatMessages,
//   fetchWpChatData,
// } from 'store/slices/whatsappChatSlice';
// import Button from 'components/form/Button';

// const ChatContainer = ({ name, paperId, whatsapp_number, user_id }) => {
//   const dispatch = useDispatch();
//   const userData = getDataFromLocalStorage();
//   // const { id: myID } = userData || {};

//   // Refs
//   const msgRef = useRef(null);
//   const htmlElRef = useRef(null);

//   const [msg, setMsg] = useState('');
//   const [fileLoader, setFileLoader] = useState(false);
//   const [activeStatus, setActiveStatus] = useState(false);
//   const [messageList, setMessageList] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [fileType, setFileType] = useState('Msg');
//   const [selectedFileName, setSelectedFileName] = useState('');

//   const recieverName = name ? name : '';
//   const profile_photo_path = 'https://via.placeholder.com/150';

//   const getWpChatData = async () => {
//     const payload = {
//       phone_no: whatsapp_number,
//       user_id: user_id,
//     };
//     const response = await dispatch(fetchWpChatData(payload));
//     if (response.status === 200) {
//       setMessageList(response?.data);
//       setIsLoading(false);
//     }
//   };

//   useEffect(() => {
//     getWpChatData();
//   }, [whatsapp_number, user_id]);

//   useEffect(() => {
//     if (msgRef.current) {
//       msgRef.current.scrollIntoView({ behavior: 'smooth' });
//     }
//   }, [messageList]);

//   const handelSendMessage = async () => {
//     if (msg.trim() || selectedFile) {
//       const newMessage = {
//         id: messageList.length + 1,
//         message_origin: 'AGENT',
//         message: msg,
//         created_at: new Date().toISOString(),
//         message_file: selectedFile || '',
//         status: '0',
//       };
//       const response = await dispatch(
//         sendWpChatMessages({
//           message: msg || '',
//           message_file: selectedFile || '',
//           phone_no: whatsapp_number,
//           user_id: user_id,
//           type: fileType,
//         })
//       );
//       if (response.status === 200) {
//         fileType === 'Msg'
//           ? setMessageList([...messageList, newMessage])
//           : getWpChatData();
//         setMsg('');
//         setFileLoader(false);
//         setSelectedFile(null);
//         setFileType('Msg');
//         setSelectedFileName('');
//       }
//     }
//   };

//   const handelChange = (e) => {
//     setMsg(e.target.value);
//   };

//   const handleUploadChatFile = async (e) => {
//     let file = e.target.files[0];
//     let type = file?.name?.split('.').pop();
//     // ['png', 'jpg', 'jpeg', 'mp3', 'mp4', 'txt', 'doc', 'csv', 'pdf']
//     if (['png', 'jpg', 'jpeg'].includes(type)) {
//       setFileLoader(true);
//       if (e.target.files[0]) {
//         setSelectedFileName(file.name); // Store file name
//         const base64File = await getBase64(file);
//         let fileType = ['png', 'jpg', 'jpeg'].includes(type) ? 'Image' : 'File';
//         setSelectedFile(base64File);
//         setFileType(fileType);
//         setFileLoader(false);
//       } else {
//         setFileLoader(false);
//       }
//     } else {
//       dispatch(
//         throwError({
//           message: 'Invalid file selection. accept only png, jpg, jpeg',
//         })
//       );
//     }
//   };

//   const groupMessagesByDate = (messages) => {
//     return messages.reduce((acc, message) => {
//       const messageDate = moment(message.created_at);

//       let formattedDate;
//       if (messageDate.isSame(moment(), 'day')) {
//         formattedDate = 'Today';
//       } else if (messageDate.isSame(moment().subtract(1, 'days'), 'day')) {
//         formattedDate = 'Yesterday';
//       } else {
//         formattedDate = messageDate.format('MMM DD, YYYY');
//       }

//       if (!acc[formattedDate]) {
//         acc[formattedDate] = [];
//       }
//       acc[formattedDate].push(message);

//       return acc;
//     }, {});
//   };

//   const calculateUnreadMessages = (messages) => {
//     return messages.filter(
//       (message) => message.status !== '2' && message.message_origin !== 'AGENT'
//     ).length;
//   };

//   const getLastUnreadMessages = (messages) => {
//     const unreadMessages = messages.filter(
//       (message) => message.status !== '2' && message.message_origin !== 'AGENT'
//     );
//     return unreadMessages.slice(-2);
//   };

//   const groupedMessages = groupMessagesByDate(messageList);
//   const unreadCount = calculateUnreadMessages(messageList);
//   const lastUnreadMessages = getLastUnreadMessages(messageList);

//   useEffect(() => {
//     // Configure Pusher globally
//     window.Pusher = Pusher;

//     // Initialize Laravel Echo
//     const echo = new Echo({
//       broadcaster: 'pusher',
//       key: process.env.REACT_APP_PUSHER_KEY,
//       cluster: 'ap2',
//       forceTLS: true,
//       disableStats: true,
//     });

//     // Subscribe to the channel and listen for the event
//     const channel = echo.channel('whatsapp-channel');

//     channel.listen('.NewMessageSent', (event) => {
//       // console.log('Received raw event:', event);
//       // Update state
//       // setMessageList((prevMessages) => [
//       //   ...prevMessages,
//       //   { phone: event.phone, message: event.message },
//       // ]);
//       getWpChatData();
//     });

//     // Cleanup when component unmounts
//     return () => {
//       console.log('Disconnecting from Pusher...');
//       echo.disconnect();
//     };
//   }, []);

//   return (
//     <div id="wp-chat-container">
//       <Card className="cpt-16 cpb-16 h-100 chat-message-container">
//         <div className="d-flex align-items-center justify-content-between cps-20 cpe-20 cpb-16 border-bottom-prof">
//           <div className="d-flex align-items-center chat-profile-container">
//             <div className="profile-container">
//               <Profile
//                 isRounded
//                 text={recieverName}
//                 size="s-48"
//                 url={profile_photo_path}
//                 isS3UserURL
//               />
//               <div
//                 className={`status-icon ${activeStatus ? 'online' : 'offline'}`}
//               ></div>
//             </div>
//             <div className="user-details-block ms-3">
//               <div className="text-15-600 title-text">{recieverName}</div>
//             </div>
//           </div>
//           <div>
//             <i className="bi bi-three-dots-vertical text-16-400 color-dark-silver" />
//           </div>
//         </div>
//         <div className="chat iferp-scroll">
//           <div className="chat__wrapper">
//             {Object.entries(groupedMessages).map(([date, messages]) => (
//               <React.Fragment key={date}>
//                 <div className="cmt-24 cmb-24">
//                   <LineTextLabel
//                     text={
//                       <span className="text-13-400 color-6866">{date}</span>
//                     }
//                     showBorder={false}
//                   />
//                 </div>

//                 {messages.map((elm, index) => {
//                   const isAccess = elm.message_origin === 'AGENT';
//                   const isLastUnread = lastUnreadMessages.includes(elm);

//                   return (
//                     <React.Fragment key={elm.id}>
//                       {isLastUnread && index === 0 && (
//                         <div className="cmt-10 cmb-10">
//                           <LineTextLabel
//                             text={
//                               <span className="text-14-500 color-1717">
//                                 {`${unreadCount} Unread Messages`}
//                               </span>
//                             }
//                             showBorder={false}
//                           />
//                         </div>
//                       )}

//                       <div
//                         className={`chat__message ${
//                           isAccess ? 'chat__message__own' : ''
//                         } ${
//                           elm.message_file && isAccess
//                             ? 'chat__message__msg_file'
//                             : ''
//                         }`}
//                       >
//                         <MessageBlock
//                           data={elm}
//                           // is_delete={elm.is_delete}
//                           readTickerShow={isAccess}
//                         />
//                       </div>
//                     </React.Fragment>
//                   );
//                 })}
//               </React.Fragment>
//             ))}
//             <div ref={msgRef} />
//           </div>
//         </div>
//         <div className="chat-input-container position-relative">
//           {selectedFile && (
//             <div className="file-preview-card mb-2 p-2">
//               <div className="d-flex align-items-center justify-content-between">
//                 {fileType === 'Image' ? (
//                   <div className="d-flex align-items-center">
//                     <img
//                       src={selectedFile}
//                       alt="Preview"
//                       className="img-thumbnail me-2"
//                       style={{
//                         width: '50px',
//                         height: '50px',
//                         objectFit: 'cover',
//                       }}
//                     />
//                     <span
//                       className="text-truncate"
//                       style={{ maxWidth: '150px' }}
//                     >
//                       {selectedFileName}
//                     </span>
//                   </div>
//                 ) : (
//                   <div className="d-flex align-items-center">
//                     <i className="bi bi-file-earmark me-2"></i>
//                     <span
//                       className="text-truncate"
//                       style={{ maxWidth: '200px' }}
//                     >
//                       {selectedFileName}
//                     </span>
//                   </div>
//                 )}
//                 <Button
//                   onClick={() => {
//                     setSelectedFile(null);
//                     setSelectedFileName('');
//                     setFileType('Msg');
//                   }}
//                   icon={
//                     <i className="bi bi-x-circle-fill text-danger me-2"></i>
//                   }
//                 />
//               </div>
//             </div>
//           )}
//           <div className="chat-i-block">
//             <div className="left-emoji-icon">
//               <EmojiPicker
//                 onChange={(e) => {
//                   let newMessage = `${msg} ${e?.value}`;
//                   setMsg(newMessage);
//                 }}
//               />
//             </div>
//             <input
//               autoFocus
//               value={msg}
//               ref={htmlElRef}
//               id="post-input"
//               placeholder="Write your message"
//               onChange={handelChange}
//               onKeyUp={(e) => {
//                 if (e?.keyCode === 13) {
//                   handelSendMessage();
//                 }
//               }}
//             />
//             <div className="create-post-icon-container">
//               <label className="create-post-icon">
//                 <span>
//                   {fileLoader ? (
//                     <Loader size="sm" />
//                   ) : (
//                     <img
//                       src={icons.attchment}
//                       alt="attch"
//                       className="create-post-icon"
//                     />
//                   )}
//                 </span>
//                 <input
//                   type="file"
//                   name="Select File"
//                   id="fileToUpload"
//                   onChange={handleUploadChatFile}
//                   accept=".jpg,.png,.jpeg,"
//                 />
//               </label>

//               <span className="post-send pointer" onClick={handelSendMessage}>
//                 <i className="bi bi-send" />
//               </span>
//             </div>
//           </div>
//         </div>
//       </Card>
//     </div>
//   );
// };

// export default ChatContainer;
